import { envType } from '@atlassiansox/analytics-web-client';

import { fg } from '@atlaskit/platform-feature-flags';

import { ALL_SUPPORTED_OBJECTS } from './constants';
import type { Product } from './global-object-preview-panels';
import type { GlobalObjectType } from './types';

/**
 * Route names that should use the new Work Item View
 */
const WHITELISTED_ROUTES: ReadonlySet<string> = new Set([
	// Software (JSW) - Team-Managed Routes
	'software-boards', // /jira/software/projects/:projectKey/boards/:boardId
	'software-backlog', // /jira/software/projects/:projectKey/boards/:boardId/backlog

	// Software (JSW) - Company-Managed Routes (Classic)
	'rapidboard-board', // /jira/software/c/projects/:projectKey/boards/:boardId
	'rapidboard-backlog', // /jira/software/c/projects/:projectKey/boards/:boardId/backlog

	// Work Management (JWM) Routes
	'classic-business-board', // /jira/core/projects/:projectKey/board/:itemId?
	'work-item', // /jira/browse/:issueKey
]);

/**
 * Checks if the current route should use the new Work Item View
 */
const isRouteWhitelisted = (routeName?: string): boolean => {
	return routeName ? WHITELISTED_ROUTES.has(routeName) : false;
};

// Helper function to check if we're in an iframe - exported for testing
export const isInIframe = (): boolean => {
	try {
		return window !== window.top;
	} catch {
		// If we can't access window.top due to cross-origin restrictions, assume we're in an iframe
		return true;
	}
};

export const asGlobalObjectType = (value: string): GlobalObjectType => {
	if (!ALL_SUPPORTED_OBJECTS.includes(value as GlobalObjectType)) {
		throw new Error(`Unsupported global object type: ${value}`);
	}

	return value as GlobalObjectType;
};

/**
 * Constructs a proper embed URL by extracting query parameters and reconstructing the URL
 * @param url - The original URL
 * @param supportedObjects - Array of supported object types for previewPanels param
 * @returns A properly formatted embed URL with all query parameters at the end
 */
export const buildIssueViewEmbedUrl = (
	url: string,
	supportedObjects: string[],
	rootSourceScreen?: string,
): string => {
	try {
		const urlObj = new URL(url);

		if (fg('pol-21166-fix-404-on-jpd-preview')) {
			if (isJpdObject(urlObj)) {
				return buildJpdEmbedUrl(urlObj);
			}
		}

		// Ensure there's no trailing slash before appending '/embed'
		urlObj.pathname = urlObj.pathname.replace(/\/$/, '') + '/embed';

		// Add previewPanels to existing search params
		urlObj.searchParams.set('previewPanels', supportedObjects.join(','));

		if (rootSourceScreen && fg('add_capacity_tab_to_the_board_horizontal_nav')) {
			urlObj.searchParams.set('previewRootSourceScreen', rootSourceScreen);
		}
		// Return the full URL as a string
		return urlObj.toString();
	} catch {
		// Fallback for invalid URLs
		if (fg('add_capacity_tab_to_the_board_horizontal_nav')) {
			const encodedRootSource = rootSourceScreen
				? `&previewRootSource=${encodeURIComponent(rootSourceScreen)}`
				: '';
			return `${url}/embed?previewPanels=${encodeURIComponent(supportedObjects.join(','))}${encodedRootSource}`;
		} else {
			return `${url}/embed?previewPanels=${encodeURIComponent(supportedObjects.join(','))}`;
		}
	}
};

/**
 * Constructs a proper embed URL for Work Item Modernization with product-specific handling
 * For Jira, adds disableRedirect and removes /jira from pathname when not on whitelisted routes
 * @param url - The original URL
 * @param product - The product type (e.g., 'jira', 'confluence')
 * @param supportedObjects - Array of supported object types for previewPanels param
 * @param routeName - The current route name from the router
 * @returns A properly formatted embed URL with all query parameters at the end
 */
export const buildIssueViewEmbedUrlForWorkItemModernization = (
	url: string,
	product: Product,
	supportedObjects: string[],
	routeName?: string,
	rootSourceScreen?: string,
): string => {
	try {
		const urlObj = new URL(url);

		if (fg('pol-21166-fix-404-on-jpd-preview')) {
			if (isJpdObject(urlObj)) {
				return buildJpdEmbedUrl(urlObj);
			}
		}

		if (product === 'jira' && !isRouteWhitelisted(routeName)) {
			// Add disableRedirect to existing search params
			urlObj.searchParams.set('disableRedirect', 'true');

			// Remove /jira from the pathname if it exists
			if (urlObj.pathname.includes('/jira/browse')) {
				urlObj.pathname = urlObj.pathname.replace(/\/jira/, '');
			}
		}

		// Ensure there's no trailing slash before appending '/embed'
		urlObj.pathname = urlObj.pathname.replace(/\/$/, '') + '/embed';

		// Add previewPanels to existing search params
		urlObj.searchParams.set('previewPanels', supportedObjects.join(','));

		if (rootSourceScreen && fg('add_capacity_tab_to_the_board_horizontal_nav')) {
			urlObj.searchParams.set('previewRootSourceScreen', rootSourceScreen);
		}
		// Return the full URL as a string
		return urlObj.toString();
	} catch {
		// Fallback for invalid URLs
		const shouldAddDisableRedirectParam = product === 'jira' && !isRouteWhitelisted(routeName);
		const redirectParam = shouldAddDisableRedirectParam ? 'disableRedirect=true&' : '';
		if (fg('add_capacity_tab_to_the_board_horizontal_nav')) {
			const encodedRootSource = rootSourceScreen
				? `&previewRootSource=${encodeURIComponent(rootSourceScreen)}`
				: '';
			return `${url}/embed?${redirectParam}previewPanels=${encodeURIComponent(supportedObjects.join(','))}${encodedRootSource}`;
		} else {
			return `${url}/embed?${redirectParam}previewPanels=${encodeURIComponent(supportedObjects.join(','))}`;
		}
	}
};

export const getAnalyticsEnv = (hostname: string): envType.DEV | envType.PROD => {
	if (hostname.includes('localhost') || hostname.includes('jira-dev')) {
		return envType.DEV;
	}

	return envType.PROD;
};

const isJpdObject = (urlObj: URL) => {
	return urlObj.pathname.includes('/jira/polaris/projects/');
};

const buildJpdEmbedUrl = (urlObj: URL) => {
	const ideaKey = urlObj.searchParams.get('selectedIssue');
	urlObj.searchParams.delete('selectedIssue');

	urlObj.pathname = `/browse/${ideaKey}/embed`;

	return urlObj.toString();
};
